import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getSalesReps, deleteSalesRep, patchSalesRep, logError } from '../api';
import { Button, Table, Container, Row, Col } from 'react-bootstrap';
import DeleteConfirmationModal from './Global/DeleteConfirmationModal';
import DisabledToggle from './ui/DisabledToggle';
import DisabledRowStyles from './ui/DisabledRowStyles';
import SortableHeader from './ui/SortableHeader';
import { useDisabledToggle } from '../hooks/useDisabledToggle';
import { useSorting } from '../hooks/useSorting';
import { getDisabledRowClass } from '../utils/disabledUtils';
import './Global/Style/SalesRepsList.css';

const SalesRepList = () => {
    const [salesReps, setSalesReps] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [repToDelete, setRepToDelete] = useState(null);
    const { includeDisabled, setIncludeDisabled } = useDisabledToggle();
    const { orderBy, orderDesc, handleSort, getSortIcon } = useSorting();

    const loadSalesReps = useCallback(async () => {
        try {
            const data = await getSalesReps(includeDisabled, orderBy, orderDesc);
            setSalesReps(data);
        } catch (error) {
            logError(error);
            console.error('Error loading sales reps:', error);
        }
    }, [includeDisabled, orderBy, orderDesc]);

    useEffect(() => {
        loadSalesReps();
    }, [loadSalesReps]);

    const handleDeleteConfirm = () => {
        if (repToDelete) {
            deleteSalesRep(repToDelete)
                .then(() => {
                    setSalesReps(salesReps.filter(rep => rep.id !== repToDelete));
                    setShowModal(false);
                })
                .catch(err => logError(err));
        }
    };

    const handleToggleStatus = async (repId, currentStatus) => {
        try {
            await patchSalesRep(repId, { id: repId, disabled: !currentStatus });
            setSalesReps((prevReps) => 
                prevReps.map((rep) => 
                    rep.id === repId 
                        ? { ...rep, disabled: !currentStatus }
                        : rep
                )
            );
        } catch (error) {
            logError(error);
            console.error('Error toggling sales rep status:', error);
        }
    };

    return (
        <>
            <DisabledRowStyles />
            <Container fluid className="my-4">
                <Row className="mb-4">
                    <Col xs={12} md={6}>
                        <h1>Comerciales</h1>
                    </Col>
                    <Col xs={12} md={6} className="d-flex justify-content-end align-items-center">
                        <DisabledToggle 
                            includeDisabled={includeDisabled}
                            onChange={setIncludeDisabled}
                        />
                        <Button variant="primary" data-testid="new-salesrep-button">
                            <Link to="/salesreps/new" style={{ color: 'white', textDecoration: 'none' }}>
                                Nuevo Comercial
                            </Link>
                        </Button>
                    </Col>
                </Row>

            {/* Lista de representantes */}
            <Table responsive className="table">
                <thead>
                    <tr>
                        <SortableHeader column="id" onSort={handleSort} getSortIcon={getSortIcon}>Id</SortableHeader>
                        <SortableHeader column="abbrev" onSort={handleSort} getSortIcon={getSortIcon}>Abreviatura</SortableHeader>
                        <SortableHeader column="name" onSort={handleSort} getSortIcon={getSortIcon}>Nombre</SortableHeader>
                        <th>Username</th>
                        <SortableHeader column="phone" onSort={handleSort} getSortIcon={getSortIcon}>Teléfono</SortableHeader>
                        <SortableHeader column="telegramName" onSort={handleSort} getSortIcon={getSortIcon}>Telegram</SortableHeader>
                        <SortableHeader column="email" onSort={handleSort} getSortIcon={getSortIcon}>Email</SortableHeader>
                        <SortableHeader column="commissionPercentage" onSort={handleSort} getSortIcon={getSortIcon}>Comisión</SortableHeader>
                        <th>Color</th>
                        <th>¿Es jefe?</th>
                        <th>Logo personalizado</th>
                        <th>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {salesReps.map(rep => (
                        <tr key={rep.id} className={getDisabledRowClass(rep.disabled)}>
                            <td data-label="Id">{rep.id}</td>
                            <td data-label="Abreviatura">{rep.abbrev}</td>
                            <td data-label="Nombre">{rep.name} {rep.lastName1}</td>
                            <td data-label="Username">{rep.user?.username}</td>
                            <td data-label="Teléfono">{rep.phone}</td>
                            <td data-label="Telegram">{rep.telegramName}</td>
                            <td data-label="Email">{rep.email}</td>
                            <td data-label="Commission">
                                {`${(rep.commissionPercentage).toLocaleString(undefined, {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 2
                                })}%`}
                            </td>

                            <td className="text-center align-middle"> 
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                    <div style={{ width: '25px', height: '25px', backgroundColor: rep.colourHex, border: '1px solid #000', borderRadius: '50%' }}></div>
                                </div>
                            </td>
                            <td data-label="¿Es Jefe?">{rep.user?.roles?.some(r => r.id === 6) ? 'Sí' : 'No'}</td>
                            <td data-label="Logo personalizado">{rep.useCustomLogo ? 'Sí' : 'No'}</td>
                            <td>
                                <div className="text-center align-middle">
                                    <Button variant="warning" className="me-2" as={Link} to={`/salesreps/edit/${rep.id}`} size="sm" data-testid={`edit-salesrep-${rep.id}`}>
                                        Editar
                                    </Button>
                                    <Button
                                        variant={rep.disabled ? "success" : "secondary"}
                                        onClick={() => handleToggleStatus(rep.id, rep.disabled)}
                                        size="sm"
                                        className="me-2"
                                        data-testid={`toggle-salesrep-${rep.id}`}
                                    >
                                        {rep.disabled ? "Habilitar" : "Deshabilitar"}
                                    </Button>
                                    <Button 
                                        variant="danger" 
                                        onClick={() => { setRepToDelete(rep.id); setShowModal(true); }}
                                        size="sm"
                                        data-testid={`delete-salesrep-${rep.id}`}
                                    >
                                        Eliminar
                                    </Button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            {/* Modal de confirmación de eliminación */}
                <DeleteConfirmationModal
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    onConfirm={handleDeleteConfirm}
                    message="¿Estás seguro de que deseas eliminar este comercial? Esta acción no se puede deshacer."
                />
            </Container>
        </>
    );
};

export default SalesRepList;
